import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Building2, 
  LifeBuoy, 
  Mail, 
  Phone, 
  Save,
  Info,
  Server,
  Lock,
  Send,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MioRepartoPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [reparto, setReparto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nome_referente_supporto: '',
    email_supporto: '',
    telefono_supporto: ''
  });
  const [smtpData, setSmtpData] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_encryption: 'SSL',
    smtp_from_name: '',
    smtp_reply_to: ''
  });

  // Fetch dati del mio reparto
  const fetchReparto = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/my-reparto`, {
        headers: getAuthHeader()
      });
      setReparto(response.data);
      setFormData({
        nome_referente_supporto: response.data.nome_referente_supporto || '',
        email_supporto: response.data.email_supporto || '',
        telefono_supporto: response.data.telefono_supporto || ''
      });
      // Carica anche i dati SMTP (senza password per sicurezza)
      setSmtpData(prev => ({
        ...prev,
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 465,
        smtp_user: response.data.smtp_user || '',
        smtp_password: '',  // Password non viene mai restituita dal server per sicurezza
        smtp_encryption: response.data.smtp_encryption || 'SSL',
        smtp_from_name: response.data.smtp_from_name || '',
        smtp_reply_to: response.data.smtp_reply_to || ''
      }));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati del reparto');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchReparto();
  }, [fetchReparto]);

  // Salva i dati di supporto
  const handleSave = async () => {
    if (!reparto) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/reparti/${reparto.id}/supporto`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      toast.success('Dati di supporto aggiornati con successo');
      fetchReparto();  // Ricarica i dati
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Testa la configurazione SMTP
  const handleTestSmtp = async () => {
    if (!reparto) return;
    
    // Validazione campi obbligatori
    if (!smtpData.smtp_host || !smtpData.smtp_port || !smtpData.smtp_user || !smtpData.smtp_password) {
      toast.error('Compila tutti i campi SMTP obbligatori (Host, Porta, Username, Password)');
      return;
    }
    
    setTestingSmtp(true);
    try {
      const response = await axios.post(`${API}/reparti/${reparto.id}/test-smtp`, smtpData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchReparto();  // Ricarica per aggiornare smtp_verified
      } else {
        toast.error(response.data.message || 'Test SMTP fallito');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il test SMTP');
      console.error(error);
    } finally {
      setTestingSmtp(false);
    }
  };

  // Salva solo la configurazione SMTP senza test
  const handleSaveSmtp = async () => {
    if (!reparto) return;
    
    setSavingSmtp(true);
    try {
      // Usa l'endpoint dedicato per Top Agent
      await axios.put(`${API}/reparti/${reparto.id}/smtp`, smtpData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      toast.success('Configurazione SMTP salvata (non verificata). Usa "Invia Test" per verificare.');
      fetchReparto();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio SMTP');
      console.error(error);
    } finally {
      setSavingSmtp(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!reparto) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Nessun reparto associato al tuo account.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canEdit = user?.role === 'top_agent' || user?.role === 'super_admin';

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <Building2 className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{reparto.nome}</h1>
          <p className="text-muted-foreground">Gestione dati del tuo reparto</p>
        </div>
      </div>

      {/* Info Reparto */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Informazioni Reparto
          </CardTitle>
          <CardDescription>Dati generali del reparto (sola lettura)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <Label className="text-xs text-muted-foreground">Nome Reparto</Label>
              <p className="font-medium">{reparto.nome}</p>
            </div>
            {reparto.descrizione && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs text-muted-foreground">Descrizione</Label>
                <p className="font-medium">{reparto.descrizione}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dati Supporto (modificabili) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <LifeBuoy className="w-5 h-5" />
            Dati Supporto
          </CardTitle>
          <CardDescription>
            Questi dati saranno visibili a tutti gli utenti del reparto nel widget di assistenza in alto a destra.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="nome_referente">Nome Referente</Label>
            <Input 
              id="nome_referente"
              value={formData.nome_referente_supporto}
              onChange={(e) => setFormData({...formData, nome_referente_supporto: e.target.value})}
              placeholder="es. Mario Rossi"
              disabled={!canEdit}
              data-testid="input-nome-referente"
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="email_supporto" className="flex items-center gap-1">
              <Mail className="w-4 h-4" />
              Email Supporto
            </Label>
            <Input 
              id="email_supporto"
              type="email"
              value={formData.email_supporto}
              onChange={(e) => setFormData({...formData, email_supporto: e.target.value})}
              placeholder="es. supporto@azienda.it"
              disabled={!canEdit}
              data-testid="input-email-supporto"
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="telefono_supporto" className="flex items-center gap-1">
              <Phone className="w-4 h-4" />
              Telefono Supporto
            </Label>
            <Input 
              id="telefono_supporto"
              value={formData.telefono_supporto}
              onChange={(e) => setFormData({...formData, telefono_supporto: e.target.value})}
              placeholder="es. +39 02 12345678"
              disabled={!canEdit}
              data-testid="input-telefono-supporto"
            />
          </div>

          {canEdit && (
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full mt-4"
              data-testid="save-supporto-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salva Dati Supporto
                </>
              )}
            </Button>
          )}

          {!canEdit && (
            <p className="text-sm text-muted-foreground text-center mt-4">
              Solo il Top Agent del reparto può modificare questi dati.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ============== CONFIGURAZIONE SMTP REPARTO ============== */}
      {canEdit && (
        <Card className="border-2 border-dashed border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Server className="w-5 h-5" />
              Configurazione Email SMTP
              {reparto.smtp_verified && (
                <span className="ml-2 flex items-center gap-1 text-green-600 text-sm font-normal">
                  <CheckCircle2 className="w-4 h-4" />
                  Verificato
                </span>
              )}
              {reparto.smtp_host && !reparto.smtp_verified && (
                <span className="ml-2 flex items-center gap-1 text-amber-600 text-sm font-normal">
                  <XCircle className="w-4 h-4" />
                  Non Verificato
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Configura un server SMTP dedicato per inviare email con la tua identità aziendale.
              Se non configurato o se l'invio fallisce, il sistema userà automaticamente <code className="bg-slate-100 px-1 rounded">preventivi@machenoleggio.it</code> come fallback.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Riga 1: Host e Porta */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="smtp_host">Host SMTP *</Label>
                <Input 
                  id="smtp_host"
                  value={smtpData.smtp_host}
                  onChange={(e) => setSmtpData({...smtpData, smtp_host: e.target.value})}
                  placeholder="es. smtps.aruba.it"
                  data-testid="input-smtp-host"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="smtp_port">Porta *</Label>
                <Input 
                  id="smtp_port"
                  type="number"
                  value={smtpData.smtp_port}
                  onChange={(e) => setSmtpData({...smtpData, smtp_port: parseInt(e.target.value) || 465})}
                  placeholder="465"
                  data-testid="input-smtp-port"
                />
              </div>
            </div>

            {/* Riga 2: Username e Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="smtp_user" className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  Username (Email) *
                </Label>
                <Input 
                  id="smtp_user"
                  type="email"
                  value={smtpData.smtp_user}
                  onChange={(e) => setSmtpData({...smtpData, smtp_user: e.target.value})}
                  placeholder="es. info@tuodominio.it"
                  autoComplete="off"
                  data-testid="input-smtp-user"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="smtp_password" className="flex items-center gap-1">
                  <Lock className="w-4 h-4" />
                  Password *
                </Label>
                <div className="relative">
                  <Input 
                    id="smtp_password"
                    type={showPassword ? "text" : "password"}
                    value={smtpData.smtp_password}
                    onChange={(e) => setSmtpData({...smtpData, smtp_password: e.target.value})}
                    placeholder="Password SMTP"
                    autoComplete="new-password"
                    data-testid="input-smtp-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {reparto.smtp_host && (
                  <p className="text-xs text-muted-foreground">
                    Lascia vuoto per mantenere la password esistente
                  </p>
                )}
              </div>
            </div>

            {/* Riga 3: Criptazione e Nome Mittente */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="smtp_encryption">Criptazione</Label>
                <Select 
                  value={smtpData.smtp_encryption} 
                  onValueChange={(value) => setSmtpData({...smtpData, smtp_encryption: value})}
                >
                  <SelectTrigger data-testid="select-smtp-encryption">
                    <SelectValue placeholder="Seleziona criptazione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SSL">SSL (porta 465)</SelectItem>
                    <SelectItem value="TLS">STARTTLS (porta 587)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="smtp_from_name">Nome Visualizzato Mittente</Label>
                <Input 
                  id="smtp_from_name"
                  value={smtpData.smtp_from_name}
                  onChange={(e) => setSmtpData({...smtpData, smtp_from_name: e.target.value})}
                  placeholder="es. RentWell Noleggi"
                  data-testid="input-smtp-from-name"
                />
                <p className="text-xs text-muted-foreground">
                  Il nome che appare al destinatario (es. "RentWell" invece di "info@rentwell.it")
                </p>
              </div>
            </div>

            {/* Riga 4: Reply-To */}
            <div className="grid gap-2">
              <Label htmlFor="smtp_reply_to" className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Reply-To (Risposta a)
              </Label>
              <Input 
                id="smtp_reply_to"
                type="email"
                value={smtpData.smtp_reply_to}
                onChange={(e) => setSmtpData({...smtpData, smtp_reply_to: e.target.value})}
                placeholder="es. commerciale@tuodominio.it (opzionale)"
                data-testid="input-smtp-reply-to"
              />
              <p className="text-xs text-muted-foreground">
                Indirizzo dove il cliente risponderà. Se vuoto, verrà usata l'email dell'agente.
              </p>
            </div>

            {/* Pulsanti Azione */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button 
                onClick={handleTestSmtp} 
                disabled={testingSmtp || !smtpData.smtp_host || !smtpData.smtp_password}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="test-smtp-btn"
              >
                {testingSmtp ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Invio test in corso...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Invia Test & Salva
                  </>
                )}
              </Button>
              <Button 
                onClick={handleSaveSmtp} 
                disabled={savingSmtp}
                variant="outline"
                className="flex-1"
                data-testid="save-smtp-btn"
              >
                {savingSmtp ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salva senza Test
                  </>
                )}
              </Button>
            </div>

            {/* Info Fallback SMTP */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4">
              <p className="text-sm text-blue-800">
                <strong>📧 Logica Fallback:</strong> Se l'invio tramite il tuo SMTP fallisce (credenziali errate, timeout, ecc.), 
                il sistema switcherà automaticamente su <code className="bg-blue-100 px-1 rounded">preventivi@machenoleggio.it</code> 
                per garantire che l'email venga comunque recapitata al cliente.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Fallback */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <strong>💡 Nota:</strong> Se questi campi sono vuoti, il sistema mostrerà automaticamente 
          i dati di supporto della Sede Centrale come fallback.
        </p>
      </div>
    </div>
  );
};

export default MioRepartoPage;
